import React, { useEffect, useState } from 'react';

import { isEmptyObject, isString } from 'Utilities/helpers';
import type { PhotoCommonType, PhotoType } from 'Utilities/profiling/@types';
import { SECTION_STATES } from 'Utilities/sectionState';

import { useVehicleProfilingData } from '../DataFlow/VehicleProfilingContext.helpers';
import type { PhotoInput } from '../Photos/ImageUpload/photosSections';
import { photoSections } from '../Photos/ImageUpload/photosSections';

import { TextType } from './ConditionBlock';
import {
  DAMAGE,
  LocalTCopy,
} from './ConfirmDetails.helpers';

const useGetSectionComplete = (isReadOnly?: boolean, sectionState?: string) => {
  const [isActive, setIsActive] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const active = !isReadOnly && (sectionState === SECTION_STATES.ACTIVE);
    const complete = !isReadOnly && (sectionState === SECTION_STATES.COMPLETED);
    const isReadOnlyOrCompleted = isReadOnly || complete;

    setIsActive(active);
    setIsComplete(complete);
    setCompleted(isReadOnlyOrCompleted);
  }, [isReadOnly, sectionState]);

  return {
    completed, isActive, isComplete,
  };
};

const { ELECTRICAL_MECHANICAL, OTHER_DAMAGE, TYRES, WHEELS } = DAMAGE;

const damagedSides = (type: string, conditionObj: Record<string, PhotoCommonType[]>) =>
  (!isEmptyObject(conditionObj)
    ? photoSections?.[type]?.inputs?.filter((x) =>
      (conditionObj[x?.name]?.[0] as PhotoType)?.isDamaged) : []);

const useConditionCopy = () => {
  const { premiumOfferData } = useVehicleProfilingData();
  const { vehicleDetails } = premiumOfferData || {};

  type TextProps = {
    conditionObj: Record<string, PhotoCommonType[]>;
    keyForDetails: keyof typeof vehicleDetails;
    x: PhotoInput
  }

  const yes = LocalTCopy('conditionDetails.yes') as string;
  const no = LocalTCopy('conditionDetails.n/a') as string;

  const renderText = ({ conditionObj, keyForDetails, x }: TextProps) => {
    const value = vehicleDetails?.[keyForDetails];

    if (value) {
      if (x.name === WHEELS.VALUE) {
        return damagedSides(WHEELS.NAME, conditionObj);
      }
      if (x.name === TYRES.VALUE) {
        return damagedSides(TYRES.NAME, conditionObj);
      }

      if (x.name === ELECTRICAL_MECHANICAL.VALUE) {
        const valueDesc = vehicleDetails?.electricsProblemsDesc;
        return value && valueDesc?.length ? yes : [];
      }

      if (x.name === OTHER_DAMAGE.VALUE) {
        return String(value)?.length ? yes : [];
      }
    }

    return [];
  };

  const renderValue = (text: TextType) => {
    if (Array.isArray(text) && text?.length) {
      return text?.map((value) => <p key={value.name}>{value.label}</p>);
    }

    if (isString(text)) {
      return <p>{String(text)}</p>;
    }

    return <p>{no}</p>;
  };

  return { renderText, renderValue };
};

export { useGetSectionComplete, useConditionCopy };
